import { createElement, useState } from "react";
import { Trans } from "react-i18next";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { getSettings } from "@common/settingsProvider";
import { downloadFile } from "@common/api/downloadFile";
import { createPdfUrl } from "@common/pdfUrlHelper";
import { useRiskAreasContext } from "@common/providers/riskAreasProvider/RiskAreasProvider";
import { useTranslation } from "@localization/useTranslation";
import { useUrlProvider } from "../../useUrlProvider";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { RiskGovernanceMode } from "../riskArea/RiskGovernanceModeSwitcher";
import { RiskGovernanceFilter } from "./RiskGovernanceOverview.types";
import {
    deriveAfterMitigation,
    riskGovernanceAsOfKey,
    riskGovernanceFilterKey,
    riskGovernanceModeKey,
    riskGovernanceSearchKey,
} from "./RiskGovernanceOverview.hooks";
import { RiskGovernancePdfState } from "./riskGovernanceOverviewPdf/RiskGovernanceOverviewPdf.hooks";

function readSession<T>(key: string, fallback: T): T {
    const item = sessionStorage.getItem(key);
    return item ? (JSON.parse(item) as T) : fallback;
}

// The printed overview must mirror what the user is looking at, but it renders in a fresh headless browser with
// no session storage. So the current view state (filters, search, mode, snapshot date) is read back from the
// per-area session-storage keys the overview writes and carried into the PDF route as an encoded query param.
export function useRiskGovernancePdfDownload(riskAreaId: string | undefined) {
    const { translateString } = useTranslation();
    const { getRiskGovernanceOverviewPdfUrl } = useUrlProvider();
    const snackBar = useDotLegalSnackbar();
    const { language } = useUserContext();
    const { riskAreas } = useRiskAreasContext();
    const [isDownloading, setIsDownloading] = useState(false);

    const onDownload = async () => {
        if (!riskAreaId) return;
        setIsDownloading(true);

        const filter = readSession<RiskGovernanceFilter>(riskGovernanceFilterKey(riskAreaId), {});
        const searchText = readSession<string>(riskGovernanceSearchKey(riskAreaId), "");
        const mode = readSession<RiskGovernanceMode>(riskGovernanceModeKey(riskAreaId), RiskGovernanceMode.Current);
        const asOf = readSession<string | null>(riskGovernanceAsOfKey(riskAreaId), null);
        const afterMitigation = deriveAfterMitigation(mode, asOf);

        const state: RiskGovernancePdfState = { filter, searchText, afterMitigation, asOf };
        const fileName = riskAreas.find((area) => area.id === riskAreaId)?.name || translateString("riskGovernanceOverviewFileName");
        const routeUrl = getSettings().mainSiteBaseUrl + getRiskGovernanceOverviewPdfUrl(riskAreaId) + "?state=" + encodeURIComponent(JSON.stringify(state));

        await downloadFile(getSettings().apiBaseUrl + "/pdf/generate?noMargin=true&url=" + createPdfUrl(routeUrl, language), `${fileName}.pdf`);
        snackBar.show(createElement(Trans, { i18nKey: "pdfDownloadComplete", values: { fileName } }));
        setIsDownloading(false);
    };

    return { onDownload, isDownloading };
}
